/**
 * Authenticated app shell: top bar, a persistent LEFT module sidebar (a mobile
 * drawer below 900px), the per-child switcher, and the routed module outlet.
 * The per-child switcher is hidden on family-wide modules
 * (messages/expenses/agreements/sharing/audit) and on Settings to reduce noise.
 */
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useFamily } from '../family/FamilyContext';
import type { SessionUser } from '../auth/types';
import { TopBar } from './TopBar';
import { ChildrenSwitcher } from './ChildrenSwitcher';
import { ModuleSidebar } from './ModuleSidebar';
import { TourProvider } from '../tour/TourContext';
import { OnboardingTour } from '../tour/OnboardingTour';
import { TourAutoStart } from '../tour/TourAutoStart';
import {
  MODULES,
  isModuleVisibleInMode,
  type ModuleDef,
} from '../modules/registry';
import { ModuleGuard } from '../modules/ModuleGuard';
import { SettingsScreen } from '../screens/SettingsScreen';
import { HomeModule } from '../modules/home/HomeModule';
import { ChildrenModule } from '../modules/children/ChildrenModule';
import { ContactsModule } from '../modules/contacts/ContactsModule';
import { HealthModule } from '../modules/health/HealthModule';
import { CalendarModule } from '../modules/calendar/CalendarModule';
import { ShoppingModule } from '../modules/shopping/ShoppingModule';
import { DocumentsModule } from '../modules/documents/DocumentsModule';
import { MessagesModule } from '../modules/messages/MessagesModule';
import { ExpensesModule } from '../modules/expenses/ExpensesModule';
import { AgreementsModule } from '../modules/agreements/AgreementsModule';
import { SharingModule } from '../modules/sharing/SharingModule';
import { AuditModule } from '../modules/audit/AuditModule';
import type { FamilyMode, Membership } from '@solandia/core';

const COMPONENTS: Record<string, () => JSX.Element> = {
  home: HomeModule,
  children: ChildrenModule,
  contacts: ContactsModule,
  health: HealthModule,
  calendar: CalendarModule,
  shopping: ShoppingModule,
  documents: DocumentsModule,
  messages: MessagesModule,
  expenses: ExpensesModule,
  agreements: AgreementsModule,
  sharing: SharingModule,
  audit: AuditModule,
};

/**
 * The slug to land on. Prefers Home (visible to any active member); falls back to
 * the first scope-and-mode-accessible module, then to contacts as a last resort.
 */
function landingSlug(membership: Membership, mode: FamilyMode): string {
  const m = MODULES.find((mod) => isModuleVisibleInMode(mod, membership, mode));
  return m?.slug ?? 'contacts';
}

export function AppShell({ user }: { user: SessionUser }) {
  const { t } = useI18n();
  const { membership, family } = useFamily();
  const location = useLocation();
  const segment = location.pathname.split('/')[1] ?? '';
  const slug = location.pathname.split('/')[2] ?? '';
  const activeModule = MODULES.find((m) => m.slug === slug);
  const onSettings = segment === 'settings';
  // The per-child switcher only makes sense on per-child modules.
  const showChildren = !onSettings && activeModule?.perChild === true;

  return (
    <TourProvider>
      <div className="app-shell">
        <a href="#main" className="skip-link">
          {t.common.skipToContent}
        </a>
        <TopBar user={user} />

        <div className="shell-body container">
          <ModuleSidebar />

          <main id="main" className="shell-main">
            {showChildren ? (
              <div className="shell-children" data-tour="children">
                <ChildrenSwitcher />
              </div>
            ) : null}

            <Routes>
              <Route
                index
                element={<Navigate to={`/m/${landingSlug(membership, family.mode)}`} replace />}
              />
              {MODULES.filter((mod) => mod.modes.includes(family.mode)).map((mod) => (
                <Route
                  key={mod.slug}
                  path={`m/${mod.slug}`}
                  element={<ModuleRoute module={mod} />}
                />
              ))}
              <Route path="settings" element={<SettingsScreen />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

            <footer className="shell-footer">
              <span className="text-xs muted">
                {t.appName} · {t.tagline}
              </span>
            </footer>
          </main>
        </div>
      </div>

      {/* First-login walkthrough: auto-starts once, replayable from Settings. */}
      <TourAutoStart />
      <OnboardingTour />
    </TourProvider>
  );
}

function ModuleRoute({ module }: { module: ModuleDef }) {
  const Component = COMPONENTS[module.slug]!;
  return (
    <ModuleGuard module={module}>
      <Component />
    </ModuleGuard>
  );
}
